// Imports
import React, { useEffect, useRef, useState } from 'react';
import { NavigationType, Params, useLocation, useNavigationType, useParams } from 'react-router-dom';
import commonActions from '../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch, useAppSelector } from '../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../shareables/integrations/api/redux/actions';
import { GetPageResponse } from '../../../../../shareables/types/api/main/website/page/get';
import NotFound from '../not-found';
import BoardMembers from './board-members';
import Calendar from './calendar';
import Committees from './committees';
import Documents from './documents';
import NewsArticle from './news-articles/news-article';
import NewsArticles from './news-articles/news-articles';
import Location from './location';
import PhotoGallery from './photo-gallery';
import PetDirectory from './pet-directory';
import ContactForm from './contact-form';
import CustomForm from './custom-form';
import Faqs from './faqs';
import GarageSales from './garage-sales';
import Recipes from './recipes';
import Recipe from './recipes/recipe';
import ResidentDirectory from './resident-directory';
import InsufficientPrivileges from '../insufficient-privileges';
import CommunityFacilities from './community-facilities';
import RequireAuthentication from '../../../shared-components/require-authentication';
import AccountDetails from './resident-directory/details';
import PageTypesLink from './link';
import UnavailableFeature from '../unavailable-feature';
import Forum from './forum';
import Topic from './forum/topic';
import UsefulContactInformation from './useful-contact-information';
import GeneralText from './general-text';
import ResidentAlerts from './resident-alerts';
import SurveyResults from './survey-results';
import Classifieds from './classifieds';
import Listing from './classifieds/listing';
import PageTitle from '../shared-components/page-title';
import LeadingText from '../shared-components/leading-text';
import useMemberCheck from '../../../../../utils/use-member-check';


// Function component
const PageTypesRoutes = () => {
	// Use state
	const [page, setPage] = useState<GetPageResponse | null>(null);
	const [pageNotFound, setPageNotFound] = useState(false);
	const [hasPrivilege, setHasPrivilege] = useState(true);
	const [insufficientPlan, setInsufficientPlan] = useState(false);
	
	
	// Use ref
	const headerRef = useRef<HTMLHeadingElement>(null);
	const prevPathnameRef = useRef<string | undefined>();
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const pages = useAppSelector((state) => state.self.pages);
	const signedIn = useAppSelector((state) => state.common.user.signedIn);
	const canEditPage = useMemberCheck({ type: 'HAS_ACCESS', hasAccess: page?.canEdit ?? false });
	const hasAccountIssue = useMemberCheck({ type: 'ACCOUNT_ISSUE' });
	
	
	// Use React Router functionality
	const location = useLocation();
	const navigationType = useNavigationType();
	const { partialURI, articleIdentifier, recipeIdentifier, accountIdentifier, topicIdentifier, listingIdentifier } =
		useParams<Params>();
	
	
	// Initialize
	const selectedPage = pages?.find((page) => page.uri === `/p/${partialURI ?? ''}`);
	
	
	// Load page on mount
	useEffect(() => {
		if ((!signedIn && selectedPage?.hasRestrictions) || !pages) {
			return;
		}
		
		
		// Reset state
		setPageNotFound(false);
		setHasPrivilege(true);
		setInsufficientPlan(false);
		
		void dispatch(
			apiActions.call<undefined, undefined, GetPageResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/uri_${partialURI ?? ''}`,
				handleErrorsAutomatically: false,
				completion: (json) => {
					// Check for response errors
					if (json.status === 'error') {
						// Handle expected error messages
						if (json.message.toLowerCase().includes('page not found.')) {
							setPageNotFound(true);
							return;
						}
						
						if (json.message.toLowerCase().includes('you don’t have the necessary privileges to use this feature.')) {
							setHasPrivilege(false);
							return;
						}
						
						if (/your community must be on the \w+ plan to use this feature/i.test(json.message)) {
							setInsufficientPlan(true);
							return;
						}
						
						
						// Handle all other errors
						dispatch(
							commonActions.showPageMessage({
								color: 'danger',
								title: 'Oh snap!',
								message: json.message,
							})
						);
					} else {
						setPage(json.response);
					}
				},
			})
		);
	}, [dispatch, partialURI, signedIn, selectedPage?.hasRestrictions, pages]);
	
	
	// Scroll to the page heading when a panel opens or closes
	useEffect(() => {
		// Skip back/forward navigation
		if (navigationType === NavigationType.Pop) {
			return;
		}
		
		
		// Track previous pathname
		const prevPathname = prevPathnameRef.current;
		prevPathnameRef.current = location.pathname;
		
		
		// Detect panel open/close
		const isNavigatingToPanel =
			prevPathname !== undefined &&
			(location.pathname === `${prevPathname}/create` || location.pathname === `${prevPathname}/edit`);
		
		const isNavigatingFromPanel =
			prevPathname === `${location.pathname}/create` || prevPathname === `${location.pathname}/edit`;
		
		
		// Scroll to the heading when opening or closing a form panel
		if (isNavigatingToPanel || isNavigatingFromPanel) {
			headerRef.current?.scrollIntoView({ behavior: 'smooth' });
		}
	}, [location.pathname, navigationType]);
	
	
	// Handle page not found
	if (pageNotFound) {
		return <NotFound />;
	}
	
	
	// Handle if website doesn't have access to the required plan
	if (insufficientPlan) {
		return <UnavailableFeature />;
	}
	
	
	// Handle restricted pages
	if (!signedIn && (selectedPage?.hasRestrictions || !hasPrivilege)) {
		return <RequireAuthentication />;
	}
	
	
	// Handle lack of privileges
	if (!hasPrivilege || (page?.pageTypeID === 219 && hasAccountIssue)) {
		return <InsufficientPrivileges title={selectedPage?.title ?? ''} />;
	}
	
	
	// If loading, then display nothing
	if (page === null) {
		return null;
	}
	
	
	// Map page type ID's to components
	const pageTypeComponents: Record<number, JSX.Element> = {
		100: <PageTypesLink pageID={page.id} key={page.id} />,
		200: <GeneralText key={page.id} bodyText={page.text} />,
		201: <Documents pageID={page.id} key={page.id} />,
		202: <Calendar pageID={page.id} key={page.id} pageTitle={page.title} />,
		203: <BoardMembers pageID={page.id} key={page.id} />,
		204: articleIdentifier ? (
			<NewsArticle pageID={page.id} key={page.id} />
		) : (
			<NewsArticles pageID={page.id} key={page.id} />
		),
		205: <Location pageID={page.id} key={page.id} />,
		206: <PhotoGallery pageID={page.id} key={page.id} />,
		207: accountIdentifier ? (
			<AccountDetails pageID={page.id} key={page.id} pageTitle={page.title} />
		) : (
			<ResidentDirectory pageID={page.id} key={page.id} pageHeader={page.header} />
		),
		208: <PetDirectory pageID={page.id} key={page.id} />,
		209: <Committees pageID={page.id} key={page.id} />,
		210: <ContactForm pageID={page.id} key={page.id} />,
		211: <Faqs pageID={page.id} key={page.id} />,
		212: <GarageSales pageID={page.id} key={page.id} />,
		213: recipeIdentifier ? (
			<Recipe pageID={page.id} key={page.id} pageTitle={page.title} />
		) : (
			<Recipes pageID={page.id} key={page.id} />
		),
		214: <CommunityFacilities pageID={page.id} key={page.id} />,
		215: topicIdentifier ? (
			<Topic pageID={page.id} key={page.id} pageTitle={page.title} />
		) : (
			<Forum pageID={page.id} key={page.id} />
		),
		216: listingIdentifier ? (
			<Listing pageID={page.id} key={page.id} pageTitle={page.title} />
		) : (
			<Classifieds pageID={page.id} key={page.id} />
		),
		217: <UsefulContactInformation pageID={page.id} key={page.id} />,
		218: <CustomForm pageID={page.id} key={page.id} />,
		219: <ResidentAlerts pageID={page.id} key={page.id} />,
		220: <SurveyResults pageID={page.id} key={page.id} />,
	};
	
	
	// Determine if this is a subpage
	const isSubpage = Boolean(
		articleIdentifier || accountIdentifier || recipeIdentifier || topicIdentifier || listingIdentifier
	);
	
	
	// Determine if leading text should be shown
	const displayLeadingText = !isSubpage && page.pageTypeID !== 200;
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PageTitle
				{...(canEditPage && {
					editPageDetails: {
						pageID: page.id,
						pageTypeID: page.pageTypeID,
					},
				})}
				ref={headerRef}
				appendCommunityName={false}
				documentTitle={page.title}
				{...(isSubpage && { linkTo: `/p/${partialURI ?? ''}` })}
			>
				{page.header}
			</PageTitle>
			
			{displayLeadingText && page.text && <LeadingText>{page.text}</LeadingText>}
			{pageTypeComponents[page.pageTypeID]}
		</React.Fragment>
	);
};

export default PageTypesRoutes;
